/** One input or intermediate term shown in the worked example. */
export type KpiOperand = {
  /** Short symbol used in the formula line, e.g. "A" or "Σ tickets". */
  symbol: string;
  /** Human label, e.g. "Total assets". */
  label: string;
  /** Pre-formatted value with units, e.g. "€120,000". */
  value: string;
};

/** Full explanation payload for a single KPI — rendered in the explain sidebar. */
export type KpiExplain = {
  /** One sentence, plain language — shown large at the top. */
  plainEnglish: string;
  /** Prominent formula notation, e.g. "Net = Assets − Liabilities". */
  formulaHeadline: string;
  /** Optional deeper walk-through of the formula. */
  formulaDetail?: string;
  /** Terms plugged into the example calculation. */
  operands: KpiOperand[];
  /** Arithmetic with real numbers, e.g. "€500,000 − €120,000". */
  calculation: string;
  /** Final formatted KPI value. */
  result: string;
  /** Caveats, data sources, or interpretation hints. */
  notes?: string[];
};

export type KpiExplainSession = {
  title: string;
  explain: KpiExplain;
};

/** Build operand rows from numeric line items; truncates long lists. */
export function operandsFromItems(
  items: { label: string; value: number }[],
  formatValue: (value: number) => string,
  maxVisible = 4,
): KpiOperand[] {
  if (items.length === 0) return [];
  const visible = items.slice(0, maxVisible);
  const rows: KpiOperand[] = visible.map((item, index) => ({
    symbol: String.fromCharCode(65 + index),
    label: item.label,
    value: formatValue(item.value),
  }));
  if (items.length > maxVisible) {
    const rest = items.slice(maxVisible);
    const restSum = rest.reduce((sum, item) => sum + item.value, 0);
    rows.push({
      symbol: '…',
      label: `${rest.length} more`,
      value: formatValue(restSum),
    });
  }
  return rows;
}

/** Sum operands and format a Σ calculation string. */
export function sumCalculation(values: number[], formatValue: (value: number) => string): string {
  if (values.length === 0) return formatValue(0);
  if (values.length === 1) return formatValue(values[0] ?? 0);
  return values.map((v) => formatValue(v)).join(' + ');
}

export function kpiDifference(
  plainEnglish: string,
  formulaHeadline: string,
  minuend: { label: string; value: number; formatted: string },
  subtrahend: { label: string; value: number; formatted: string },
  formatResult: (value: number) => string,
  formulaDetail?: string,
  notes?: string[],
): KpiExplain {
  const result = minuend.value - subtrahend.value;
  return {
    plainEnglish,
    formulaHeadline,
    formulaDetail,
    operands: [
      { symbol: 'A', label: minuend.label, value: minuend.formatted },
      { symbol: 'B', label: subtrahend.label, value: subtrahend.formatted },
    ],
    calculation: `${minuend.formatted} − ${subtrahend.formatted}`,
    result: formatResult(result),
    notes,
  };
}

export function kpiRatio(
  plainEnglish: string,
  formulaHeadline: string,
  numerator: { label: string; value: number; formatted: string },
  denominator: { label: string; value: number; formatted: string },
  formatResult: (value: number) => string,
  formulaDetail?: string,
  notes?: string[],
): KpiExplain {
  const result = denominator.value === 0 ? 0 : numerator.value / denominator.value;
  return {
    plainEnglish,
    formulaHeadline,
    formulaDetail,
    operands: [
      { symbol: 'N', label: numerator.label, value: numerator.formatted },
      { symbol: 'D', label: denominator.label, value: denominator.formatted },
    ],
    calculation:
      denominator.value === 0
        ? '— (denominator is zero)'
        : `${numerator.formatted} ÷ ${denominator.formatted}`,
    result: formatResult(result),
    notes,
  };
}

export function kpiSum(
  plainEnglish: string,
  formulaHeadline: string,
  items: { label: string; value: number }[],
  formatValue: (value: number) => string,
  formulaDetail?: string,
  notes?: string[],
): KpiExplain {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const operands = operandsFromItems(items, formatValue);
  const values = items.map((i) => i.value);
  return {
    plainEnglish,
    formulaHeadline,
    formulaDetail,
    operands,
    calculation: sumCalculation(values, formatValue),
    result: formatValue(total),
    notes,
  };
}
